import { recoveryLock, storedKeys, type RecoveryStorage } from './storage.ts';
type Command = { requestId: string; path: string; body: string };
type State<C, R> = { pending: C | null; busy: boolean; blocked: boolean; error: string; success: R | null };
export function savedCommands<C extends Command, R>(options: {
  storage: RecoveryStorage; prefix: string; validate: (value: unknown) => asserts value is C;
  send: (command: C) => Promise<R>; definitive: (error: unknown) => boolean;
  confirmed?: (command: C) => void;
  begin?: () => () => void;
}) {
  const { storage, prefix } = options;
  let state: State<C, R> = { pending: null, busy: false, blocked: false, error: '', success: null };
  let end: (() => void) | null = null;
  let initialized = false;
  const listeners = new Set<() => void>();
  const protect = () => {
    if (state.pending && !end) end = options.begin?.() || (() => {});
    if (!state.pending && end) { end(); end = null; }
  };
  const update = (values: Partial<State<C, R>>) => {
    state = { ...state, ...values };
    if (initialized) protect();
    listeners.forEach(fn => fn());
  };
  const restore = () => {
    for (const key of storedKeys(storage, prefix)) {
      const raw = storage.getItem(key);
      if (raw === null) continue;
      const command: unknown = JSON.parse(raw);
      options.validate(command);
      if (key !== prefix + command.requestId) throw new Error('Invalid saved action');
      return command;
    }
    return null;
  };
  const reload = () => {
    try {
      const pending = restore();
      update({ pending, blocked: false, ...(state.pending && !pending ? { error: '' } : {}) });
      return true;
    }
    catch { update({ blocked: true, error: 'Saved actions could not be read. Restore browser storage before making changes. Do not clear it while a save is unconfirmed.' }); return false; }
  };
  reload();
  initialized = true;
  const send = async () => {
    const pending = state.pending;
    if (!pending) return;
    try {
      const result = await options.send(pending);
      options.confirmed?.(pending);
      // Do not erase a changed local record, even after a successful response.
      const stored = storage.getItem(prefix + pending.requestId);
      if (stored !== null && stored !== JSON.stringify(pending)) throw new Error('The saved action changed in this browser. Its receipt needs review.');
      storage.removeItem(prefix + pending.requestId);
      if (storage.getItem(prefix + pending.requestId) !== null) throw new Error('The confirmed action could not be cleared from this browser. Retry the same saved action.');
      update({ pending: null, success: result });
      reload();
    } catch (error) {
      if (options.definitive(error)) {
        try {
          const stored = storage.getItem(prefix + pending.requestId);
          if (stored !== null && stored !== JSON.stringify(pending)) throw new Error('Saved action changed');
          storage.removeItem(prefix + pending.requestId);
          if (storage.getItem(prefix + pending.requestId) !== null) throw new Error('The rejected action could not be cleared');
          update({ pending: null }); reload();
        } catch { update({ blocked: true }); }
      }
      update({ error: error instanceof Error ? error.message : 'The result is unknown. Retry the saved action.' });
    }
  };
  const run = async (action: () => Promise<void>) => {
    if (state.busy) return;
    update({ busy: true, error: '', success: null });
    try { await recoveryLock(prefix, action); }
    catch { update({ blocked: true, error: 'This browser could not keep the saved action. Restore browser storage before trying again.' }); }
    finally { update({ busy: false }); }
  };
  return {
    snapshot: () => state,
    // A restored request acquires update protection when the UI subscribes,
    // outside React's render, or when the user explicitly retries it.
    subscribe: (fn: () => void) => { listeners.add(fn); protect(); return () => { listeners.delete(fn); }; },
    refreshSaved: () => { if (!state.busy) reload(); },
    retry: () => run(async () => { if (reload()) await send(); }),
    submit: (command: C) => {
      if (state.pending || state.busy) return;
      return run(async () => {
        if (!reload()) return;
        if (state.pending) { update({ error: 'Confirm the saved action before making another change.' }); return; }
        options.validate(command);
        const encoded = JSON.stringify(command), key = prefix + command.requestId;
        storage.setItem(key, encoded);
        if (storage.getItem(key) !== encoded) throw new Error('Saved action could not be read back');
        update({ pending: command, blocked: false });
        await send();
      });
    },
  };
}
